import React, { useState, useEffect, useMemo } from 'react';
import './UserManagement.css';
import Button from '../../components/button';
import CustomInput from '../../components/customInput/CustomInput';
import CustomSelect from '../../components/customSelect/CustomSelect';
import CustomPaginationTable from '../../components/table/CustomPaginationTable';
import EyeIcon from '../../static/image/eyeIcon.svg';
import EditIcon from '../../static/image/editIcon.svg';
import DeleteIcon from '../../static/image/deleteIcon.svg';
import CreateUser from '../createUser/CreateUser';
import { useDispatch, useSelector } from 'react-redux';
import { fetchUsers, deleteUser, fetchUserDetails } from '../../redux/userSlice';
import { fetchRoles } from '../../redux/createUserSlice';
import { EDIT_MODEL_CREATE, EDIT_MODEL_EDIT, EDIT_MODEL_VIEW } from '../../utils/constant';
import CommonDeleteModal from '../../components/commonDeleteModal/CommonDeleteModal';
import { formatDatetime } from '../../utils/dateUtils';
import { decodedJWTPayload } from '../../utils/commonUtil';

function UserManagement() {
  const [inputValue1, setInputValue1] = useState('');
  const [selectedRoleId, setSelectedRoleId] = useState('');
  const [pagination, setPagination] = useState({
    page: 0,
    size: 10
  });
  const [editMode, setEditMode] = useState(undefined);
  const [selectedUser, setSelectedUser] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [permission, setPermission] = useState('');

  const dispatch = useDispatch();
  const { data, loading, pagination: reduxPagination } = useSelector((state) => state.users);
  const { roles } = useSelector((state) => state.createUser);

  useEffect(() => {
    dispatch(fetchUsers(pagination));
    dispatch(fetchRoles());
    const token = localStorage.getItem('access_token');
    if (token) {
      const payload = decodedJWTPayload(token);
      const mnId = payload?.mn_id;

      if (mnId) {
        const match = mnId.match(/\|010100-([RWRX]+)\|/);
        if (match) {
          setPermission(match[1]);
        }
      }
    }
  }, [dispatch]);

  const allRoleOptions = useMemo(() => {
    return roles.map((role) => ({
      value: role.roleId.toString(),
      label: role.roleName
    }));
  }, [roles]);

  const columns = [
    {
      name: 'Username',
      cell: (row) => <div>{row.login}</div>
    },
    {
      name: 'Role',
      cell: (row) => <div>{row.roleName}</div>
    },
    {
      name: 'Status',
      cell: (row) => <div>{row.isActive ? 'Active' : 'In-Active'}</div>
    },
    {
      name: 'Created By',
      cell: (row) => <div>{row.createdBy}</div>
    },
    {
      name: 'Created Datetime',
      cell: (row) => <div>{formatDatetime(row.createdDatetime)}</div>
    },
    {
      name: 'Updated By',
      cell: (row) => <div>{row.updatedBy}</div>
    },
    {
      name: 'Updated Datetime',
      cell: (row) => <div>{formatDatetime(row.updatedDatetime)}</div>
    },
    {
      name: 'Actions',
      cell: (row) => (
        <div style={{ display: 'flex', gap: '10px' }}>
          <img src={EyeIcon} alt="Eye" onClick={() => handleViewUser(row.userId)} style={{ cursor: 'pointer' }} />
          <img
            src={EditIcon}
            alt="Edit"
            onClick={() => handleEditUser(row.userId)}
            style={{ opacity: permission.includes('W') ? 1 : 0.5, cursor: permission.includes('W') ? 'pointer' : 'default' }}
          />
          <img
            src={DeleteIcon}
            alt="Delete"
            onClick={() => handleDeleteClick(row)}
            style={{ opacity: permission === 'RWX' ? 1 : 0.5, pointerEvents: permission === 'RWX' ? 'auto' : 'none', cursor: permission === 'RWX' ? 'pointer' : 'default' }}
          />
        </div>
      ),
      sortable: false
    }
  ];

  const handlePageChange = (page) => {
    const newPagination = { ...pagination, page: page - 1 }; // Convert to 0-based index
    setPagination(newPagination);
    dispatch(fetchUsers({
      ...newPagination,
      login: inputValue1,
      roleId: selectedRoleId
    }));
  };

  const handlePerRowsChange = (newPerPage, page) => {
    const newPagination = { page: page - 1, size: newPerPage }; // Convert to 0-based index
    setPagination(newPagination);
    dispatch(fetchUsers({
      ...newPagination,
      login: inputValue1,
      roleId: selectedRoleId
    }));
  };

  const handleCreateUser = () => {
    if (permission.includes('W')) {
      setEditMode(EDIT_MODEL_CREATE);
      setSelectedUser(undefined);
    }
  };

  const handleDeleteClick = (user) => {
    if (permission === 'RWX') {
      setSelectedUser(user);
      setShowDeleteModal(true);
    }
  };

  const handleConfirmDelete = () => {
    dispatch(deleteUser(selectedUser.userId))
      .then(() => {
        dispatch(fetchUsers({
          ...pagination,
          login: inputValue1,
          roleId: selectedRoleId
        }));
        setShowDeleteModal(false);
      })
      .catch((error) => {
        console.error('Delete user failed:', error);
        setShowDeleteModal(false);
      });
  };

  const handleSearch = () => {
    const newPagination = { ...pagination, page: 0 }; // Reset to first page
    setPagination(newPagination);
    dispatch(fetchUsers({
      ...newPagination,
      login: inputValue1,
      roleId: selectedRoleId
    }));
  };

  const handleViewUser = async (userId) => {
    try {
      const userDetails = await dispatch(fetchUserDetails(userId)).unwrap();
      setSelectedUser(userDetails.resdata);
      setEditMode(EDIT_MODEL_VIEW);
    } catch (error) {
      console.error('Failed to fetch user details:', error);
    }
  };

  const handleEditUser = async (userId) => {
    if (permission.includes('W')) {
      try {
        const userDetails = await dispatch(fetchUserDetails(userId)).unwrap();
        setSelectedUser(userDetails.resdata);
        setEditMode(EDIT_MODEL_EDIT);
      } catch (error) {
        console.error('Failed to fetch user details:', error);
      }
    }
  };

  const handleReset = () => {
    setInputValue1('');
    setSelectedRoleId('');
    const newPagination = { ...pagination, page: 0 };
    setPagination(newPagination);
    dispatch(fetchUsers(newPagination));
  };

  return (
    <>
      {!editMode && (
        <div className="manage-user-container">
          <div style={{ padding: '30px' }}>
            <div>Manage User</div>
            <div style={{ display: 'flex', alignItems: 'center', marginTop: '30px', gap: '20px', flexWrap: 'wrap' }}>
              <Button
                className="manage-user-btn"
                onClick={handleCreateUser}
                style={{ opacity: permission.includes('W') ? 1 : 0.5, pointerEvents: permission.includes('W') ? 'auto' : 'none', cursor: permission.includes('W') ? 'pointer' : 'default' }}
              >
                CREATE USER
              </Button>
              <div>
                <CustomInput
                  placeholder="Username"
                  style={{ width: '240px', height: '45px' }}
                  value={inputValue1}
                  onChange={(e) => setInputValue1(e.target.value)}
                />
              </div>
              <div>
                <CustomSelect
                  placeholder="Role Name"
                  option={allRoleOptions}
                  onChange={(e) => setSelectedRoleId(e.target.value)}
                  selected={selectedRoleId}
                  style={{ width: '240px', height: '45px' }}
                />
              </div>
              <Button className="manage-user-btn" onClick={handleSearch} style={{ cursor: 'pointer' }}>
                SEARCH
              </Button>
              <Button className="manage-user-btn" onClick={handleReset} style={{ cursor: 'pointer' }}>
                RESET
              </Button>
            </div>
            <div style={{ marginTop: '20px' }}>
              <CustomPaginationTable
                data={data}
                totalRows={reduxPagination?.total || 0}
                loading={loading}
                perPage={pagination.size}
                currentPage={pagination.page + 1} // Convert to 1-based index
                onPageChange={handlePageChange}
                columns={columns}
                onPerRowsChange={handlePerRowsChange}
                isHiddenPag={false}
              />
            </div>
          </div>
        </div>
      )}
      {editMode && (
        <CreateUser
          editMode={editMode}
          user={selectedUser}
          onCancel={() => setEditMode(undefined)}
          onUserCreated={() => {
            dispatch(fetchUsers(pagination));
            setEditMode(undefined);
          }}
          onUserUpdated={() => {
            dispatch(fetchUsers(pagination));
            setEditMode(undefined);
          }}
        />
      )}
      <CommonDeleteModal
        show={showDeleteModal}
        onHide={() => setShowDeleteModal(false)}
        onConfirm={handleConfirmDelete}
      />
    </>
  );
}

export default UserManagement;